import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/lib/CartContext';
import { api, formatPrice, getSessionId } from '@/lib/api';
import SEOHead from '@/components/SEOHead';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function CheckoutPage() {
    const { items, total, clearCart, itemCount } = useCart();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [orderId, setOrderId] = useState('');
    const [paymentUrl, setPaymentUrl] = useState('');
    const [form, setForm] = useState({
        customer_name: '', customer_phone: '', customer_email: '',
        customer_address: '', customer_city: 'Buenos Aires', customer_notes: '',
    });

    useEffect(() => {
        if (items.length > 0) {
            api.post('/carts', {
                session_id: getSessionId(),
                items: items.map(i => ({ product_id: i.product_id, product_name: i.product_name, price: i.price, quantity: i.quantity })),
                total,
            }).catch(() => {});
        }
    }, [items, total]);

    if (items.length === 0 && !success) {
        navigate('/carrito');
        return null;
    }

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.customer_name || !form.customer_phone || !form.customer_address) {
            toast.error('Completa los campos obligatorios');
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/orders', {
                ...form,
                items: items.map(i => ({ product_id: i.product_id, product_name: i.product_name, price: i.price, quantity: i.quantity })),
                total,
                session_id: getSessionId(),
            });
            setOrderId(res.data.order_id);
            setPaymentUrl(res.data.payment_url || '');
            setSuccess(true);
            clearCart();
            toast.success('Pedido realizado exitosamente');
        } catch (e) {
            toast.error('Error al realizar el pedido. Intenta nuevamente.');
        }
        setLoading(false);
    };

    if (success) {
        return (
            <>
                <SEOHead title="Pedido Confirmado" />
                <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="order-success">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-cp-success/10 rounded-full mb-6">
                        <CheckCircle size={40} className="text-cp-success" />
                    </div>
                    <h1 className="text-2xl font-heading font-bold text-cp-text-main mb-3">Pedido Confirmado</h1>
                    <p className="text-cp-text-body mb-2">Tu pedido <span className="font-bold">#{orderId?.slice(0, 8)}</span> ha sido recibido.</p>
                    <p className="text-cp-text-muted text-sm mb-6">Te enviamos el comprobante por WhatsApp con los detalles de tu compra.</p>

                    {paymentUrl && (
                        <div className="bg-cp-primary-soft border border-cp-border rounded-xl p-4 mb-6 text-left">
                            <p className="text-sm font-semibold text-cp-text-main mb-2">Paga tu pedido online</p>
                            <p className="text-xs text-cp-text-muted mb-3">Usa el link de pago seguro para completar tu compra de forma inmediata.</p>
                            <a
                                href={paymentUrl}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="payment-link"
                                className="inline-flex items-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all"
                            >
                                <ExternalLink size={15} />
                                Pagar ahora
                            </a>
                        </div>
                    )}

                    <button
                        onClick={() => navigate('/')}
                        data-testid="back-home-btn"
                        className="bg-cp-bg-alt hover:bg-cp-border text-cp-text-body px-6 py-3 rounded-xl font-semibold text-sm transition-all"
                    >
                        Volver al Inicio
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <SEOHead title="Finalizar Compra" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="checkout-page">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-cp-text-main mb-8">Finalizar Compra</h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6" data-testid="checkout-form">
                        <div className="bg-white border border-cp-border rounded-2xl p-6">
                            <h2 className="font-heading font-bold text-cp-text-main mb-4">Datos de Contacto</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="customer_name" className="text-cp-text-body text-sm mb-1.5 block">Nombre completo *</Label>
                                    <Input id="customer_name" name="customer_name" value={form.customer_name} onChange={handleChange}
                                        data-testid="input-name" required className="border-cp-border focus:border-cp-primary" />
                                </div>
                                <div>
                                    <Label htmlFor="customer_phone" className="text-cp-text-body text-sm mb-1.5 block">Telefono / WhatsApp *</Label>
                                    <Input id="customer_phone" name="customer_phone" value={form.customer_phone} onChange={handleChange}
                                        data-testid="input-phone" required placeholder="+54 11..." className="border-cp-border focus:border-cp-primary" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label htmlFor="customer_email" className="text-cp-text-body text-sm mb-1.5 block">Email (opcional)</Label>
                                    <Input id="customer_email" name="customer_email" type="email" value={form.customer_email} onChange={handleChange}
                                        data-testid="input-email" className="border-cp-border focus:border-cp-primary" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-cp-border rounded-2xl p-6">
                            <h2 className="font-heading font-bold text-cp-text-main mb-4">Direccion de Envio</h2>
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="customer_address" className="text-cp-text-body text-sm mb-1.5 block">Direccion completa *</Label>
                                    <Input id="customer_address" name="customer_address" value={form.customer_address} onChange={handleChange}
                                        data-testid="input-address" required placeholder="Calle, numero, piso, depto..." className="border-cp-border focus:border-cp-primary" />
                                </div>
                                <div>
                                    <Label htmlFor="customer_city" className="text-cp-text-body text-sm mb-1.5 block">Ciudad / Localidad</Label>
                                    <Input id="customer_city" name="customer_city" value={form.customer_city} onChange={handleChange}
                                        data-testid="input-city" className="border-cp-border focus:border-cp-primary" />
                                </div>
                                <div>
                                    <Label htmlFor="customer_notes" className="text-cp-text-body text-sm mb-1.5 block">Notas del pedido (opcional)</Label>
                                    <textarea id="customer_notes" name="customer_notes" rows={3} value={form.customer_notes} onChange={handleChange}
                                        data-testid="input-notes"
                                        className="w-full px-3 py-2 border border-cp-border rounded-lg text-sm text-cp-text-main placeholder:text-cp-text-muted resize-none focus:border-cp-primary focus:outline-none"
                                        placeholder="Indicaciones especiales, horarios de entrega..." />
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-order-btn"
                            className="w-full flex items-center justify-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white py-4 rounded-xl font-semibold transition-all active:scale-95"
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                            {loading ? 'Procesando...' : 'Confirmar Pedido'}
                        </button>
                    </form>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white border border-cp-border rounded-2xl p-6 sticky top-24" data-testid="order-summary">
                            <h2 className="font-heading font-bold text-cp-text-main text-lg mb-4">Tu Pedido</h2>
                            <div className="space-y-3 mb-4">
                                {items.map(item => (
                                    <div key={item.product_id} className="flex justify-between text-sm">
                                        <span className="text-cp-text-body truncate mr-2">{item.product_name} x{item.quantity}</span>
                                        <span className="text-cp-text-main font-semibold shrink-0">{formatPrice(item.price * item.quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-cp-border-soft pt-3">
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-cp-text-body">{itemCount} productos</span>
                                    <span className="font-semibold">{formatPrice(total)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-heading font-bold text-cp-text-main">Total</span>
                                    <span className="font-heading font-bold text-xl text-cp-brand">{formatPrice(total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
